// frontend/src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CheckCircle, Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { authAPI } from '../services/api';
import weplLogo from '../assets/images/wepl_logo.png';
import PasswordField, { PasswordRequirements } from '../components/UI/PasswordField';
import {
  DEFAULT_STAFF_PASSWORD,
  PASSWORD_IDENTITY_HELPER,
  passwordComplexityError,
  passwordMeetsComplexity,
} from '../utils/passwordPolicy';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  // Lock body scroll while mounted so the auth viewport behaves as a fixed
  // app shell (matches LoginPage / ForgotPassword) — see "App Shell & Safe
  // Area" in design-system-reference.md.
  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const newPassword = watch('newPassword', '');
  const confirmPassword = watch('confirmPassword', '');
  const complexityMet = passwordMeetsComplexity(newPassword);
  const canReset = Boolean(
    complexityMet &&
    newPassword !== DEFAULT_STAFF_PASSWORD &&
    confirmPassword &&
    confirmPassword === newPassword &&
    !isSubmitting
  );

  useEffect(() => {
    const validateToken = async () => {
      if (!uid || !token) {
        setValidationError('Invalid reset link. Please request a new password reset.');
        setIsValidating(false);
        return;
      }

      try {
        const response = await authAPI.validateResetToken(uid, token);
        if (response.data.valid) {
          setIsValid(true);
        } else {
          setValidationError(response.data.error || 'This reset link has expired.');
        }
      } catch (err) {
        setValidationError('This reset link has expired or is invalid. Please request a new one.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [uid, token]);

  const onSubmit = async (data) => {
    if (!passwordMeetsComplexity(data.newPassword) || data.newPassword === DEFAULT_STAFF_PASSWORD) {
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      await authAPI.resetPassword(uid, token, data.newPassword);
      setIsSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sharedWrapper = (children) => (
    <div className="h-dvh overflow-auto overscroll-contain bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-4 py-6">
      {children}
    </div>
  );

  // Validating state
  if (isValidating) {
    return sharedWrapper(
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-800 mx-auto mb-3" />
        <p className="text-sm text-gray-600">Validating reset link…</p>
      </div>
    );
  }

  // Invalid token state
  if (!isValid && !isSuccess) {
    return sharedWrapper(
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl shadow-soft px-6 py-8 text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Link Expired</h2>
          <p className="text-sm text-gray-500 mb-6">{validationError}</p>
          <Link
            to="/forgot-password"
            className="btn btn-primary w-full mb-3"
          >
            Request New Link
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return sharedWrapper(
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl shadow-soft px-6 py-8 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Password Reset!</h2>
          <p className="text-sm text-gray-500 mb-6">
            Your password has been changed. You can now sign in with your new password.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-primary w-full"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="h-dvh overflow-auto overscroll-contain bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-sm space-y-5">

        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-20 h-20 flex items-center justify-center overflow-hidden">
              <img
                src={weplLogo}
                alt="WEPL Logo"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">Reset Password</h1>
          <p className="mt-1 text-sm text-gray-500">Enter your new password below.</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-soft px-6 py-7">
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>

            <div>
              <PasswordField
                id="newPassword"
                label="New Password"
                helper={PASSWORD_IDENTITY_HELPER}
                required
                placeholder="Enter new password"
                autoComplete="new-password"
                error={errors.newPassword?.message}
                {...register('newPassword', {
                  required: 'Password is required',
                  validate: (value) => {
                    if (value === DEFAULT_STAFF_PASSWORD) {
                      return 'This password is not allowed. Please choose a different password.';
                    }
                    return passwordComplexityError(value) || true;
                  },
                })}
              />
              {newPassword && !errors.newPassword && (
                <PasswordRequirements password={newPassword} />
              )}
            </div>

            <div>
              <PasswordField
                id="confirmPassword"
                label="Confirm Password"
                required
                placeholder="Confirm new password"
                autoComplete="new-password"
                error={errors.confirmPassword?.message}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === newPassword || 'Passwords do not match',
                })}
              />
              {confirmPassword && confirmPassword === newPassword && (
                <div className="mt-1.5 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
                  <span className="text-xs text-green-700">Passwords match</span>
                </div>
              )}
            </div>

            {/* Error Banner */}
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                className="bg-error-50 border border-error-200 rounded-lg px-3 py-2.5"
              >
                <p className="text-sm text-error-600">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!canReset}
              className="btn btn-primary w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  Resetting…
                </>
              ) : (
                'Reset Password'
              )}
            </button>

            {/* Back to Login */}
            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

export default ResetPassword;
